import React, { useState, useEffect } from 'react';
import { bookingAPI } from '../../services/api';
import './BookingConfirmation.scss';

const BookingConfirmation = ({ bookingData, onClose, onGoToStep }) => {
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState('');
  const [isSlotConflict, setIsSlotConflict] = useState(false);

  useEffect(() => {
    submitBooking();
  }, []);

  const submitBooking = async () => {
    try {
      const response = await bookingAPI.create(bookingData);
      setBooking(response.data.data);
      setLoading(false);
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to create booking. Please try again.';
      setError(message);
      if (message.toLowerCase().includes('already booked') || message.toLowerCase().includes('time slot')) {
        setIsSlotConflict(true);
      }
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${period}`;
  };

  if (loading) {
    return (
      <div className="booking-confirmation">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Confirming your appointment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="booking-confirmation">
        <div className="error-state">
          <div className="error-icon">✕</div>
          <h2>Booking Failed</h2>
          <p>{error}</p>
          <div className="action-buttons">
            {isSlotConflict && onGoToStep ? (
              <>
                <button className="dashboard-btn btn-secondary" onClick={onClose}>
                  Close
                </button>
                <button className="dashboard-btn btn-primary" onClick={() => onGoToStep(5)}>
                  Choose Another Time
                </button>
              </>
            ) : (
              <>
                <button className="dashboard-btn btn-secondary" onClick={onClose}>
                  Close
                </button>
                <button className="dashboard-btn btn-primary" onClick={() => onGoToStep ? onGoToStep(5) : onClose()}>
                  Try Again
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-confirmation">
      <div className="success-state">
        <div className="success-icon">✓</div>
        <h2>Booking Confirmed!</h2>
        <p className="success-message">Your appointment has been successfully booked.</p>

        <div className="booking-details">
          <h3>Appointment Details</h3>
          <div className="detail-item">
            <span className="label">Booking Reference:</span>
            <span className="value">#{booking?._id?.slice(-8) || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <span className="label">Name:</span>
            <span className="value">
              {booking?.firstName && booking?.lastName 
                ? `${booking.firstName} ${booking.lastName}`
                : booking?.patientName || bookingData?.firstName && bookingData?.lastName
                ? `${bookingData.firstName} ${bookingData.lastName}`
                : bookingData?.patientName || 'N/A'}
            </span>
          </div>
          {booking?.locationName || bookingData?.locationName && (
            <div className="detail-item">
              <span className="label">Location:</span>
              <span className="value">{booking?.locationName || bookingData?.locationName}</span>
            </div>
          )}
          <div className="detail-item">
            <span className="label">Date:</span>
            <span className="value">{formatDate(booking?.appointmentDate || bookingData?.appointmentDate)}</span>
          </div>
          <div className="detail-item">
            <span className="label">Time:</span>
            <span className="value">{formatTime(booking?.timeSlot || bookingData?.timeSlot)}</span>
          </div>
          {booking?.serviceType || bookingData?.serviceType && (
            <div className="detail-item">
              <span className="label">Service:</span>
              <span className="value">{booking?.serviceType || bookingData?.serviceType}</span>
            </div>
          )}
          {booking?.practitionerName || bookingData?.practitionerName && (
            <div className="detail-item">
              <span className="label">Practitioner:</span>
              <span className="value">{booking?.practitionerName || bookingData?.practitionerName || 'Any available'}</span>
            </div>
          )}
          <div className="detail-item">
            <span className="label">Email:</span>
            <span className="value">{booking?.email || bookingData?.email}</span>
          </div>
          <div className="detail-item">
            <span className="label">Phone:</span>
            <span className="value">{booking?.phone || bookingData?.phone}</span>
          </div>
          <div className="detail-item">
            <span className="label">Status:</span>
            <span className="value status-pending">Pending Confirmation</span>
          </div>
        </div>

        <div className="confirmation-notes">
          <p>
            <strong>What's next?</strong>
          </p>
          <p>
            You will receive a confirmation email shortly. Our team will contact you to confirm your appointment.
            If you need to make any changes, please contact us at (08) 6296 2323.
          </p>
        </div>

        <div className="action-buttons">
          <button className="dashboard-btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button className="dashboard-btn btn-primary" onClick={() => {
            if (onClose) {
              onClose();
              // Small delay to allow modal to close, then reopen if needed
              setTimeout(() => {
                // Trigger a custom event to reopen the modal
                window.dispatchEvent(new CustomEvent('openBookingModal'));
              }, 300);
            }
          }}>
            Book Another Appointment
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;
